import { useState, useRef, useEffect, useCallback } from "react";

export const useAudioPlayer = () => {
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const play = useCallback((trackId: string, previewUrl: string) => {
    // If clicking the same track that's currently playing, pause it
    if (currentTrackId === trackId && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    // Stop current audio if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Create and play new audio
    const audio = new Audio(previewUrl);
    audioRef.current = audio;
    setCurrentTrackId(trackId);
    setIsPlaying(true);

    audio.play().catch((error) => {
      console.error("Error playing audio:", error);
      setIsPlaying(false);
      setCurrentTrackId(null);
    });

    // Handle when audio ends
    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setCurrentTrackId(null);
    });

    // Handle errors
    audio.addEventListener("error", () => {
      setIsPlaying(false);
      setCurrentTrackId(null);
    });
  }, [currentTrackId, isPlaying]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentTrackId(null);
  }, []);

  const isTrackPlaying = useCallback(
    (trackId: string) => currentTrackId === trackId && isPlaying,
    [currentTrackId, isPlaying]
  );

  return {
    play,
    stop,
    isTrackPlaying,
    currentTrackId,
    isPlaying,
  };
};
